import type { Transaction } from "@rocicorp/zero";
import { schema, type Schema } from "./generatedDrizzleSchema";
import { userFacingError } from "./userFacingError";

export type TableName = keyof Schema["tables"];
export type InsertRow<T extends TableName> = Parameters<
  Transaction["mutate"][T]["insert"]
>[0];

// Postgres caps a statement at 65535 parameters.
const MAX_PARAMETERS = 60000;

// The generated schema only carries `serverName` where it differs from the Zero
// name, and types it per table, so read it through one loose shape.
type SchemaTable = {
  serverName?: string;
  primaryKey: readonly string[];
  columns: Record<string, { serverName?: string; type: string }>;
};

function schemaTable(table: TableName): SchemaTable {
  return schema.tables[table] as unknown as SchemaTable;
}

export function serverTable(table: TableName): string {
  return schemaTable(table).serverName ?? table;
}

export function serverColumn(table: TableName, column: string): string {
  return schemaTable(table).columns[column]?.serverName ?? column;
}

/**
 * Writes many rows to one table: a single multi-row statement on the server,
 * per-row optimistic writes on the client. A caller that derives the same row
 * twice keeps the first unless conflicts are rejected.
 */
export async function insertRows<T extends TableName>(
  tx: Transaction,
  table: T,
  rows: Array<InsertRow<T>>,
  options?: { onConflict: "reject" }
): Promise<void> {
  const unique = dedupeByPrimaryKey(table, rows);
  const rejectConflicts = options?.onConflict === "reject";
  if (rejectConflicts && unique.length !== rows.length) {
    throw userFacingError("This ID is already in use. Try again.");
  }
  if (unique.length === 0) return;

  if (tx.location !== "server") {
    for (const row of unique) await tx.mutate[table].insert(row as never);
    return;
  }

  const columns = [...new Set(unique.flatMap((row) => Object.keys(row)))];
  const columnList = columns
    .map((column) => serverColumn(table, column))
    .join(", ");
  const perStatement = Math.floor(MAX_PARAMETERS / columns.length);
  const conflictTarget = rejectConflicts
    ? ` (${schemaTable(table)
        .primaryKey.map((column) => serverColumn(table, column))
        .join(", ")})`
    : "";

  for (let start = 0; start < unique.length; start += perStatement) {
    const chunk = unique.slice(start, start + perStatement);
    let placeholder = 0;
    const values = chunk
      .map(() => `(${columns.map(() => `$${++placeholder}`).join(", ")})`)
      .join(", ");
    const returning = rejectConflicts ? " RETURNING 1" : "";
    const inserted = await tx.dbTransaction.query(
      `INSERT INTO ${serverTable(table)} (${columnList}) VALUES ${values} ON CONFLICT${conflictTarget} DO NOTHING${returning}`,
      chunk.flatMap((row) =>
        columns.map((column) => sqlValue(table, column, row))
      )
    );
    if (rejectConflicts && Array.from(inserted).length !== chunk.length) {
      throw userFacingError("This ID is already in use. Try again.");
    }
  }
}

export async function insertNewRow<T extends TableName>(
  tx: Transaction,
  table: T,
  row: InsertRow<T>
): Promise<void> {
  await insertRows(tx, table, [row], { onConflict: "reject" });
}

function dedupeByPrimaryKey<T extends TableName>(
  table: T,
  rows: Array<InsertRow<T>>
): Array<InsertRow<T>> {
  const key = schemaTable(table).primaryKey;
  const seen = new Set<string>();
  return rows.filter((row) => {
    const identity = JSON.stringify(
      key.map((column) => (row as Record<string, unknown>)[column])
    );
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

function sqlValue<T extends TableName>(
  table: T,
  column: string,
  row: InsertRow<T>
): unknown {
  const value = (row as Record<string, unknown>)[column];
  // A row may omit a key another row in the batch has, and postgres.js rejects
  // undefined outright. Nullish stays SQL NULL rather than the JSON literal
  // `null` a nullable json column would otherwise get.
  if (value == null) return null;
  // postgres.js sends a JS array as a Postgres array, never as JSON.
  return schemaTable(table).columns[column]?.type === "json"
    ? JSON.stringify(value)
    : value;
}
